
import { Component, Vue, Watch } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import FeedbackRepositoryInterface, {
  FeedbackInterface,
} from '../../../../services/QA/feedback/interfaces';
import { DataTableHeader } from 'vuetify';
import { ChatInterface, ChatsWSMessageInterface } from '@/services/QA/chat/interfaces';
import { UserBaseInfoInterface, UserRepoInterface } from '@/services/Users/Interfaces';
import { TicketInterface, TicketsWSMessageInterface } from '@/services/QA/ticket/interfaces';

@Component({
  mixins: [
    Themeable,
  ],
  inject: [
    'feedbackRepository',
    'userRepository',
  ],
  sockets: {
    chatsData(msg: ChatsWSMessageInterface): void {
      this.$emit('updateChats', msg.chats);
    },
    ticketsData(msg: TicketsWSMessageInterface): void {
      this.$emit('ticketsUpdate', msg.tickets);
    },
  },
})
export default class AllFeedbacks extends Vue {
  private feedbackRepository: FeedbackRepositoryInterface;
  private feedbacks: FeedbackInterface[] = [];
  private loadedFeedbacks: FeedbackInterface[] = [];

  private loading: boolean = true;
  private responseReceived: boolean = false;
  private chatsMessageReceived: boolean = false;
  private ticketsMessagesReceived: boolean = false;

  private search: string = '';
  private userRepository!: UserRepoInterface;
  private usersMap: { [userId: string]: UserBaseInfoInterface } = {};

  private totalItems: number = 0;
  private currentPage: number = 1;
  private itemsPerPage: number = 10;

  private sortBy: string = 'timestamp';
  private sortDesc: boolean = true;
  private options: { sortBy: string[], sortDesc: boolean[] } = { sortBy: [this.sortBy], sortDesc: [this.sortDesc] };

  private headers: DataTableHeader[] = [
    { text: 'Status', value: 'status' },
    { text: 'Type', value: 'type', sortable: false },
    { text: 'Resource ID', value: 'resource', sortable: false },
    { text: 'External', value: 'external', sortable: false },
    { text: 'Date & Time', value: 'timestamp' },
    { text: 'Tags', value: 'tags', sortable: false },
    { text: 'Feedback From', value: 'feedbackFromUserId', sortable: false },
    { text: 'Feedback To', value: 'feedbackToUserId', sortable: false },
    { text: 'Client', value: 'client', sortable: false },
    { text: 'Actions', value: 'actions', sortable: false },
  ];

  public async beforeCreate(): Promise<void> {
    this.$on('updateChats', async (chats: ChatInterface[]) => {
      this.chatsMessageReceived = true;
      await this.assignFeedbacksChatData(chats);
    });

    this.$on('ticketsUpdate', async (tickets: TicketInterface[]) => {
      this.ticketsMessagesReceived = true;
      await this.assignFeedbacksTicketsData(tickets);
    });
  }

  public async created(): Promise<void> {
    await this.initialize();
  }

  private async initialize(): Promise<void> {
    this.loading = true;
    this.feedbacks = [];
    const [feedbacks, totalItems] = await this.feedbackRepository
      .getAllFeedbacks(this.currentPage, this.sortBy, this.sortDesc);

    this.totalItems = totalItems;
    this.responseReceived = true;
    this.assignFeedbacksData(feedbacks);
  }

  private async assignFeedbacksTicketsData(tickets: TicketInterface[]): Promise<void> {
    if (!this.responseReceived) {
      setTimeout(() => this.assignFeedbacksTicketsData(tickets), 100);
      return;
    }

    this.feedbacks.forEach((feedback, feedbackIndex) => {
      if (!feedback.ticketFeedback) {
        return;
      }
      const index = tickets.findIndex((ticket) => ticket.id === feedback.ticketFeedback.ticketId);
      if (index !== -1) {
        Object.assign(this.feedbacks[feedbackIndex].ticketFeedback.ticket, tickets[index]);
      }
    });
    // tickets.forEach((ticket) => {
    //   const index = this.feedbacks.findIndex(
    //     (feedback) => !!feedback.ticketFeedback && feedback.ticketFeedback.ticketId === ticket.id,
    //   );
    //   if (index !== -1) {
    //     Object.assign(this.feedbacks[index].ticketFeedback.ticket, ticket);
    //   }
    // });

    await this.getUsersInfo();
  }

  private async assignFeedbacksChatData(chats: ChatInterface[]): Promise<void> {
    if (!this.responseReceived) {
      setTimeout(() => this.assignFeedbacksChatData(chats), 100);
      return;
    }
    this.feedbacks.map((feedback) => {
      const index = chats.findIndex((chat) => !!feedback.chatFeedback && feedback.chatFeedback.chatId === chat.id);
      if (index !== -1) {
        Object.assign(feedback.chatFeedback.chat, chats[index]);
      }

      return feedback;
    });
    // chats.forEach((chat) => {
    //   const index = this.feedbacks.findIndex(
    //     (feedback) => !!feedback.chatFeedback && feedback.chatFeedback.chatId === chat.id,
    //   );
    //   if (index !== -1) {
    //     Object.assign(this.feedbacks[index].chatFeedback.chat, chat);
    //   }
    // });

    await this.getUsersInfo();
  }

  private assignFeedbacksData(feedbacks: FeedbackInterface[]): void {
    feedbacks.forEach((feedback) => {
      const index = this.feedbacks.findIndex((feed) => feed.id === feedback.id);
      if (index === -1) {
        this.feedbacks.push(feedback);
        return;
      }

      Object.assign(this.feedbacks[index], feedback);
    });
  }

  private async changePage(): Promise<void> {
    if (this.currentPage < 1 || this.currentPage > Math.ceil(this.totalItems / this.itemsPerPage)) {
      return;
    }
    this.loading = true;
    this.feedbacks = [];
    if (this.search.trim() !== '') {
      await this.searchFeedbacks();
    } else {
      await this.initialize();
    }
  }

  private async searchFeedbacks(): Promise<void> {
    this.loading = true;
    this.responseReceived = false;
    this.feedbacks = [];
    let search = this.search.trim().toLowerCase();

    if (search === '') {
      await this.initialize();
      return;
    }

    Object.values(this.usersMap).forEach((user) => {
      if (user.email.includes(search)) {
        search = user.id;
      }
    });

    const [feedbacks, totalItems] = await this.feedbackRepository
      .searchFeedbacks(this.currentPage, search, this.sortBy, this.sortDesc);

    this.totalItems = totalItems;
    this.responseReceived = true;
    this.assignFeedbacksData(feedbacks);
  }

  private async getUsersInfo(): Promise<void> {
    const usersIds: string[] = [];
    const usersEmails: string [] = [];

    this.feedbacks.forEach((feedback) => {
      if (usersIds.indexOf(feedback.feedbackFromUserId) === -1) {
        usersIds.push(feedback.feedbackFromUserId);
      }

      if (usersIds.indexOf(feedback.feedbackToUserId) === -1) {
        usersIds.push(feedback.feedbackToUserId);
      }

      if (!feedback?.chatFeedback?.chat?.agents) {
        return;
      }

      feedback.chatFeedback.chat.agents.forEach((agent) => {
        if (usersEmails.indexOf(agent.login) === -1) {
          usersEmails.push(agent.login);
        }
      });
    });

    const users = (await Promise.all([
      this.userRepository.getUsersByIds(usersIds),
      this.userRepository.getUsersByEmails(usersEmails),
    ])).flat();

    users.forEach((user) => {
      this.usersMap[user.id] = user;
    });

    if (this.responseReceived && this.chatsMessageReceived && this.ticketsMessagesReceived) {
      this.loadedFeedbacks = this.feedbacks.filter((feedback) => !!feedback.chatFeedback || !!feedback.ticketFeedback);
      this.loading = false;
    }

    this.$forceUpdate();
  }

  @Watch('options')
  private async watchOptions(): Promise<void> {
    if (this.loading) {
      return;
    }

    const sortBy = this.options.sortBy[0];
    const sortDesc = this.options.sortDesc[0];

    if (!sortBy && !sortDesc) {
      this.sortBy = 'timestamp';
      this.sortDesc = true;
    } else {
      this.sortBy = sortBy;
      this.sortDesc = sortDesc;
    }

    if (this.search.trim() !== '') {
      await this.searchFeedbacks();
    } else {
      await this.initialize();
    }
  }
}
